<script setup>
// Page 7 — the fork: a volunteer treasurer and a professional VvE-beheerder have been
// reading the same page up to here, and from here they want different things. Two
// ink-bordered panels, each with its own ask: the board's is to start, the manager's
// is to read the page written for them. Nothing here is a new capability claim — it's
// the same product, described in the two vocabularies.
//
// A plain single-viewport panel rather than a scrubbed section: this is a choice, and
// asking someone to keep scrolling before both options are legible would be the one
// place on the page where motion actively costs a decision. Its reveal is the shared
// once-on-entry stagger instead.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowRight } from "@lucide/vue";
import { buttonVariants } from "@/components/ui/button";
import { useMotion, reveal, vMagnetic } from "@/lib/motion";

const { t } = useI18n();

const root = ref(null);
const panels = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    reveal(panels.value, { stagger: 0.1, trigger: root.value, start: "top 80%" });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section id="voor-wie" ref="root" class="snap-page page-panel flex items-center border-t-2 border-foreground" :data-page-label="t('landing.pager.audiences')">
    <div class="mx-auto w-full max-w-6xl px-5 py-6 sm:py-14">
      <h2 class="font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">{{ t("landing.audiences.title") }}</h2>

      <div class="mt-4 grid gap-3 sm:mt-9 sm:grid-cols-2 sm:gap-6">
        <article
          :ref="(el) => (panels[0] = el)"
          class="flex flex-col border-2 border-foreground bg-card p-3.5 shadow-[var(--shadow-hard)] sm:p-7"
        >
          <h3 class="font-sans text-base font-bold tracking-tight text-foreground sm:text-xl">{{ t("landing.audiences.board_title") }}</h3>
          <p class="mt-1.5 text-xs leading-snug text-muted-foreground sm:mt-3 sm:text-sm sm:leading-relaxed">{{ t("landing.audiences.board_body") }}</p>
          <!-- mt-auto: the two panels are different lengths, and their CTAs lining up
               is what makes them read as two equal choices rather than a main option
               and an afterthought. -->
          <div class="mt-auto pt-3 sm:pt-6">
            <span v-magnetic class="inline-block">
              <RouterLink :to="{ path: '/signup', query: { voor: 'vve' } }" :class="[buttonVariants({ size: 'sm' }), 'sm:h-11 sm:px-6 sm:text-sm']">
                {{ t("landing.audiences.board_cta") }}<ArrowRight class="size-4" />
              </RouterLink>
            </span>
          </div>
        </article>

        <article
          :ref="(el) => (panels[1] = el)"
          class="flex flex-col border-2 border-foreground bg-card p-3.5 shadow-[var(--shadow-hard)] sm:p-7"
        >
          <h3 class="font-sans text-base font-bold tracking-tight text-foreground sm:text-xl">{{ t("landing.audiences.manager_title") }}</h3>
          <p class="mt-1.5 text-xs leading-snug text-muted-foreground sm:mt-3 sm:text-sm sm:leading-relaxed">{{ t("landing.audiences.manager_body") }}</p>
          <div class="mt-auto pt-3 sm:pt-6">
            <RouterLink to="/voor-vve-beheerders" :class="[buttonVariants({ variant: 'outline', size: 'sm' }), 'sm:h-11 sm:px-6 sm:text-sm']">
              {{ t("landing.audiences.manager_cta") }}<ArrowRight class="size-4" />
            </RouterLink>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
